package com.essntl.features.service.ui.addeditservice

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import com.essntl.features.contact.ui.ContactEvent
import com.essntl.features.contact.ui.ContactState
import com.essntl.features.service.ui.addeditservice.components.*
import com.essntl.features.service.ui.addeditservice.components.AreaSectionItem
import com.essntl.features.service.ui.addeditservice.components.CategorySectionItem
import com.essntl.features.service.ui.addeditservice.components.DescriptionSectionItem
import com.essntl.features.service.ui.addeditservice.components.HeaderSectionItem
import com.essntl.features.service.ui.addeditservice.components.SubmitButton
import com.essntl.features.service.ui.addeditservice.components.TagsSectionItem
import com.essntl.features.service.ui.addeditservice.components.TitleSectionItem
import com.essntl.features.service.ui.addeditservice.components.UnsplashSectionItem
import com.essntl.uikit.components.expandable.ExpandableTabs
import com.essntl.uikit.components.scroll.EsVerticalScrollbar
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.theme.esColorScheme
import com.mohamedrejeb.richeditor.model.RichTextState

@Composable
fun EditServiceSection(
    state: AddEditServiceState,
    onEvent: (AddEditServiceEvent) -> Unit,
    richTextState: RichTextState,
    validation: AddEditServiceValidation,
    enabled: Boolean,
    contactState: ContactState,
    contactEvent: (ContactEvent) -> Unit,
    submitText: String,
    onEnableEdit: () -> Unit,
    onSubmit: (description: String) -> Unit,
    modifier: Modifier = Modifier,
) {
    var openRewriteWithAIDialog by rememberSaveable { mutableStateOf(false) }

    LaunchedEffect(state.service.value.id, state.service.value.description) {
        richTextState.setHtml(state.service.value.description ?: "")
    }

    LaunchedEffect(richTextState.annotatedString.text) {
        onEvent(AddEditServiceEvent.CheckRichTextChanges(richTextState.toHtml()))
    }

    Column(
        modifier = modifier
            .fillMaxSize()
            .background(MaterialTheme.esColorScheme.bgSecondary)
            .padding(
                vertical = 2.spacing,
            ),
    ) {
        val tabList = remember {
            listOf(
                "Details",
                "Suppliers",
            )
        }

        ExpandableTabs(
            tabList = tabList,
            id = state.service.value.id,
            modifier = Modifier
                .fillMaxWidth()
                .weight(1f),
        ) { index ->
            val lazyListState = rememberLazyListState()

            Box(
                modifier = Modifier
                    .weight(1f),
            ) {
                LazyColumn(
                    state = lazyListState,
                    contentPadding = PaddingValues(
                        horizontal = 2.5.spacing,
                    ),
                    modifier = Modifier
                        .fillMaxSize(),
                ) {
                    if (index == 0) {
                        item {
                            TitleSectionItem(
                                state = state,
                                onEvent = onEvent,
                                validation = validation,
                                enabled = enabled,
                            )
                        }

                        item {
                            HeaderSectionItem(
                                state = state,
                                onEvent = onEvent,
                                enabled = enabled,
                            )
                        }

                        item {
                            CategorySectionItem(
                                state = state,
                                onEvent = onEvent,
                                validation = validation,
                                enabled = enabled,
                            )
                        }

                        item {
                            AreaSectionItem(
                                state = state,
                                onEvent = onEvent,
                                enabled = enabled,
                            )
                        }

                        item {
                            InternalNotesItem(
                                state = state,
                                onEvent = onEvent,
                                enabled = enabled,
                            )
                        }

                        item {
                            TagsSectionItem(
                                state = state,
                                onEvent = onEvent,
                                enabled = enabled,
                            )
                        }

                        item {
                            UnsplashSectionItem(
                                state = state,
                                onEvent = onEvent,
                                enabled = enabled,
                            )
                        }

                        item {
                            DescriptionSectionItem(
                                richTextState = richTextState,
                                enabled = enabled,
                                onRewriteWithAI = {
                                    onEvent(AddEditServiceEvent.RewriteWithAI(it))
                                    openRewriteWithAIDialog = true
                                },
                            )
                        }
                    } else if (index == 1) {
                        item {
                            ServiceSupplierInformation(
                                state = state,
                                onEvent = onEvent,
                                contactState = contactState,
                                contactEvent = contactEvent,
                                enabled = enabled,
                            )
                        }
                    }

                    item {
                        Spacer(modifier = Modifier.height(1.spacing))
                    }
                }

                EsVerticalScrollbar(
                    lazyListState = lazyListState,
                    modifier =
                        Modifier
                            .align(Alignment.CenterEnd)
                            .fillMaxHeight(),
                )
            }
        }

        Box(
            contentAlignment = Alignment.Center,
            modifier = Modifier.fillMaxWidth(),
        ) {
            SubmitButton(
                richTextState = richTextState,
                state = state,
                submitText = submitText,
                onEnableEdit = onEnableEdit,
                onSubmit = onSubmit,
                enabled = enabled,
                validation = validation,
                modifier = Modifier.align(Alignment.CenterStart),
            )
        }

        RewriteWithAIPopup(
            state = state.rewriteWithAI,
            onAcceptDescription = {
                openRewriteWithAIDialog = false
                state.rewriteWithAI.data?.let {
                    richTextState.setHtml(it)
                }
            },
            onDismissRequest = {
                openRewriteWithAIDialog = false
            },
            openDialog = openRewriteWithAIDialog,
        )
    }
}
